import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError, BACKEND } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import FileUploader from "../../components/admin/FileUploader";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { SortableList, SortableItem, DragHandle } from "../../components/admin/SortableList";

const empty = { name: "", logo_url: "", website: "", description: "", is_active: true };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/admin/clients");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/clients/${editing.id}`, form);
      else await api.post("/admin/clients", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete client?")) return;
    try { await api.delete(`/admin/clients/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Clients"
        subtitle="Partner logos shown on the website."
        actions={
          <Button onClick={openNew} data-testid="add-client-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New client
          </Button>
        }
      />
      <div className="p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {items.length === 0 && <div className="col-span-full text-center py-12 text-[#4B5563]">No clients yet.</div>}
          <SortableList items={items} setItems={setItems} reorderEndpoint="/admin/reorder/clients" layout="grid">
            {items.map((it) => (
              <SortableItem key={it.id} id={it.id} as="div" className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={`client-card-${it.id}`}>
                {(handleProps) => (
                  <>
                    <div className="flex items-center gap-4">
                      <DragHandle handleProps={handleProps} />
                      <div className="w-16 h-16 rounded bg-[#F7F8F6] flex items-center justify-center overflow-hidden">
                        {it.logo_url ? (
                          <img src={`${BACKEND}${it.logo_url}`} alt={it.name} className="object-contain w-full h-full" />
                        ) : (
                          <span className="text-xs text-[#4B5563]">No logo</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#1F2937] truncate">{it.name}</div>
                        <div className="text-xs text-[#4B5563] truncate">{it.website || "—"}</div>
                      </div>
                    </div>
                    {it.description && <p className="mt-3 text-sm text-[#4B5563] line-clamp-2">{it.description}</p>}
                    <div className="mt-4 flex items-center justify-between">
                      <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${it.is_active ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"}`}>
                        {it.is_active ? "Active" : "Hidden"}
                      </span>
                      <div>
                        <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                        <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                      </div>
                    </div>
                  </>
                )}
              </SortableItem>
            ))}
          </SortableList>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" /></div>
            <div className="space-y-2"><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://" data-testid="client-website" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="client-desc" /></div>
            <FileUploader kind="image" accept="image/*" label="Logo" currentUrl={form.logo_url} onUploaded={(url) => setForm({ ...form, logo_url: url })} testId="upload-logo" />
            <p className="text-xs text-[#4B5563]">Order: drag the card on the list to reorder. New clients are added at the top.</p>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="client-active" />
              <Label>Visible on site</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-client" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
